/**
 * Preview-persona context object, types and hooks (#1383).
 *
 * Split out of PreviewPersonaContext.tsx so that the .tsx module exports ONLY
 * the provider component (keeps React Fast Refresh + the
 * `react-refresh/only-export-components` lint rule clean).
 */
import { createContext, useContext } from "react";
import type {
  PermissionFlags,
  PermissionPreset,
  PreviewPersona,
} from "../api/client";
import type { PreviewPersonaKey } from "../lib/previewPersona";

export interface PreviewPersonaContextType {
  /** The active preview persona, or null when the admin is on their own view. */
  persona: PreviewPersonaKey | null;
  /** True when an admin is currently previewing-as a persona. */
  previewing: boolean;
  /**
   * The simulated persona's effective definition (role + preset + flags), loaded
   * from the admin-only /config/permission-presets/ map. Null until loaded, or
   * when not previewing.
   */
  active: PreviewPersona | null;
  /** Enter preview-as the given persona (admins only; a no-op otherwise). */
  enterPreview: (persona: PreviewPersonaKey) => void;
  /** Exit preview and return to full Admin. */
  exitPreview: () => void;
}

export const PreviewPersonaContext = createContext<
  PreviewPersonaContextType | undefined
>(undefined);

export function usePreviewPersona(): PreviewPersonaContextType {
  const ctx = useContext(PreviewPersonaContext);
  if (!ctx) {
    throw new Error(
      "usePreviewPersona must be used within a PreviewPersonaProvider",
    );
  }
  return ctx;
}

/**
 * The "not previewing" default context.
 *
 * ``useEffectiveAuth`` / ``usePermissions`` consume the preview context so they
 * mask while an admin previews. Those hooks are used in MANY surfaces (and
 * tests) that legitimately render WITHOUT a PreviewPersonaProvider; for them the
 * answer is simply "not previewing" -- so they read the context optionally and
 * fall back to this default rather than throwing. The real provider (mounted in
 * App, inside AuthProvider) supplies the live value everywhere it matters.
 */
const NOT_PREVIEWING: PreviewPersonaContextType = {
  persona: null,
  previewing: false,
  active: null,
  enterPreview: () => {},
  exitPreview: () => {},
};

export function usePreviewPersonaOptional(): PreviewPersonaContextType {
  return useContext(PreviewPersonaContext) ?? NOT_PREVIEWING;
}

/**
 * The effective role / preset / flags the UI should gate against, accounting for
 * an active admin preview.
 *
 * When NOT previewing, returns the real user's values (admins all-true). When
 * previewing, returns the SIMULATED persona's bundle so EVERY flag/role/preset
 * predicate (CrmSidebar.itemVisible, the route guards, money gating) computes
 * against the same definition a real persona login renders -- the parity that is
 * the whole point of the preview.
 *
 * SECURITY: frontend masking only. The real token is unchanged.
 */
export interface EffectiveAuth {
  role: "agent" | "admin" | undefined;
  preset: PermissionPreset;
  flags: PermissionFlags | undefined;
  previewing: boolean;
  /**
   * D2-FRM-018 (#3714): office canvasser -- reads + canvasses the whole farm
   * book without owning an area. A per-user grant, never part of a persona, so
   * a preview-as persona always carries ``false``. Optional so older callers
   * building an EffectiveAuth by hand stay valid.
   */
  farmWideCanvass?: boolean;
  /**
   * D2-USR-010 (#4001): the user still works their own book (``has_own_book``).
   * A per-user fact, never part of a persona, so a preview-as persona carries
   * ``false``. Optional for the same reason as ``farmWideCanvass``.
   */
  hasOwnBook?: boolean;
}
